"use client";

import React, { useState } from "react";
import { Lead } from "@/lib/db";
import {
  ChevronLeft,
  ChevronRight,
  Building2,
  Sparkles,
  GripVertical,
  MessageSquare,
  Sliders,
  Package,
} from "lucide-react";
import { formatCurrencyValue, getCurrencyBadge } from "@/lib/currency";
import { WorkspacePipelineStage } from "@/lib/workspacePresets";

interface KanbanBoardProps {
  leads: Lead[];
  onSelectLead: (leadId: number) => void;
  onStatusChange: (leadId: number, newStatus: string) => void;
  onOpenChat?: (lead: Lead) => void;
  pipelineStages?: WorkspacePipelineStage[];
  onOpenSettings?: () => void;
  fieldLabels?: Record<string, string>;
}

const STAGES: Array<{ id: Lead["status"]; title: string; color: string; bg: string; border: string; automationHint: string }> = [
  {
    id: "New Lead",
    title: "New Leads",
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    automationHint: "Intake & verification queue",
  },
  {
    id: "Discovery Call",
    title: "Discovery Call",
    color: "text-violet-500",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
    automationHint: "⚡ Auto-sends discovery invite & sets 48h follow-up",
  },
  {
    id: "Proposal Sent",
    title: "Proposal Sent",
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    automationHint: "⚡ Auto-dispatches scope notification & 72h review",
  },
  {
    id: "Negotiation",
    title: "Negotiation",
    color: "text-orange-500",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    automationHint: "⚡ Auto-escalates priority to HOT & sets closing alert",
  },
  {
    id: "Closed Won",
    title: "Closed Won",
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
    automationHint: "🎉 Auto-dispatches onboarding & kickoff package",
  },
  {
    id: "Closed Lost",
    title: "Closed Lost",
    color: "text-slate-400",
    bg: "bg-slate-500/10",
    border: "border-slate-500/20",
    automationHint: "⚡ Auto-sets priority to COLD & archives lead",
  },
];

// Helper to normalize and map stages across varying legacy and tenant naming schemes
function normalizeStatusCategory(status: string | undefined | null): string {
  if (!status) return "new";
  const s = status.trim().toLowerCase().replace(/[_\-\/]/g, " ");
  if (s.includes("won") || s.includes("completed") || s.includes("executed") || s.includes("in prod") || s.includes("billed") || s.includes("hired") || s.includes("dispatched")) {
    return "won";
  }
  if (s.includes("lost") || s.includes("drop") || s.includes("cancel") || s.includes("declined") || s.includes("no bid") || s.includes("inactive")) {
    return "lost";
  }
  if (s.includes("negotiat") || s.includes("token") || s.includes("booking") || s.includes("commercial terms") || s.includes("deposit")) {
    return "negotiation";
  }
  if (s.includes("proposal") || s.includes("quote") || s.includes("quotation") || s.includes("sow") || s.includes("sample") || s.includes("engagement letter")) {
    return "proposal";
  }
  if (s.includes("discovery") || s.includes("call") || s.includes("tour") || s.includes("site visit") || s.includes("counseling") || s.includes("tasting") || s.includes("conflict") || s.includes("feasibility") || s.includes("consult")) {
    return "discovery";
  }
  if (s.includes("qualif") || s.includes("review") || s.includes("intake") || s.includes("rfq") || s.includes("inquiry") || s.includes("new") || s.includes("lead")) {
    return "new";
  }
  return s;
}

function leadMatchesStage(
  leadStatus: string | undefined | null,
  stage: { id: string; title: string; isWon?: boolean; isLost?: boolean },
  stageIdx: number,
  allStages: Array<{ id: string; title: string; isWon?: boolean; isLost?: boolean }>
): boolean {
  const rawLead = (leadStatus || "").trim().toLowerCase();
  const rawStage = (stage.id || stage.title || "").trim().toLowerCase();

  // 1. Direct match (case-insensitive)
  if (rawLead === rawStage) return true;

  // 2. Normalized category match
  const normLead = normalizeStatusCategory(leadStatus);
  const normStage = normalizeStatusCategory(stage.id || stage.title);

  if (stage.isWon && normLead === "won") return true;
  if (stage.isLost && normLead === "lost") return true;

  if (normLead === normStage) return true;

  // 3. Fallback: If lead does not match any other column, ensure it appears in the first intake column
  if (stageIdx === 0) {
    const matchesAnyOther = allStages.slice(1).some((otherStage) => {
      const otherRaw = (otherStage.id || otherStage.title || "").trim().toLowerCase();
      if (rawLead === otherRaw) return true;
      const otherNorm = normalizeStatusCategory(otherStage.id || otherStage.title);
      if (otherStage.isWon && normLead === "won") return true;
      if (otherStage.isLost && normLead === "lost") return true;
      return normLead === otherNorm;
    });
    if (!matchesAnyOther) return true;
  }

  return false;
}

export default function KanbanBoard({
  leads,
  onSelectLead,
  onStatusChange,
  onOpenChat,
  pipelineStages,
  onOpenSettings,
  fieldLabels = {},
}: KanbanBoardProps) {
  const [draggingLeadId, setDraggingLeadId] = useState<number | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"priority" | "newest" | "value" | "oldest">("priority");
  const [columnLimits, setColumnLimits] = useState<{ [stageId: string]: number }>({});

  const renderedStages =
    pipelineStages && pipelineStages.length > 0
      ? pipelineStages.map((s, idx) => ({
          id: s.name,
          title: s.name,
          isWon: s.isWon,
          isLost: s.isLost,
          color: s.color || (idx === 0 ? "text-blue-500" : idx === pipelineStages.length - 1 ? "text-slate-400" : "text-amber-500"),
          bg: s.bg || "bg-slate-800/40",
          border: s.border || "border-slate-700/60",
          automationHint: s.isWon
            ? "🎉 Closed Won / Completed"
            : s.isLost
            ? "Closed Lost / Cancelled"
            : `Pipeline Stage ${idx + 1}`,
        }))
      : STAGES;

  const handleDragStart = (e: React.DragEvent, leadId: number) => {
    setDraggingLeadId(leadId);
    e.dataTransfer.setData("text/plain", String(leadId));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggingLeadId(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stageId) {
      setDragOverStage(stageId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverStage(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetStage: Lead["status"]) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverStage(null);
    const rawId = e.dataTransfer.getData("text/plain");
    const leadId = Number(rawId) || draggingLeadId;
    setDraggingLeadId(null);
    
    if (leadId) {
      const droppedLead = leads.find((l) => l.id === leadId);
      if (droppedLead && droppedLead.status !== targetStage) {
        onStatusChange(leadId, targetStage);
      }
    }
  };

  return (
    <div className="space-y-3">
      {/* Visual Workflow Automation Guide & Controls Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-xs px-3.5 py-2.5 rounded-xl bg-primary/5 dark:bg-primary/10 border border-primary/20 text-slate-700 dark:text-slate-300">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary animate-pulse shrink-0" />
          <span>
            <strong>Pipeline Step Automation Active:</strong> Drag and drop any card anywhere in a column to dispatch automation, adjust priorities, and log audit trail entries.
          </span>
        </div>
        
        {/* Sort & Quick Controls */}
        <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
          {onOpenSettings && (
            <button
              type="button"
              onClick={onOpenSettings}
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 transition-colors cursor-pointer"
            >
              <Sliders className="w-3.5 h-3.5 text-primary" />
              <span>Customize Pipeline</span>
            </button>
          )}

          <span className="text-[11px] text-slate-500 font-semibold">Sort:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-2.5 py-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-semibold text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary/50"
          >
            <option value="priority">🔥 Hot Priority First</option>
            <option value="newest">⏱️ Newest First</option>
            <option value="value">💰 Highest Deal Value</option>
            <option value="oldest">📅 Oldest Inquiries First</option>
          </select>
        </div>
      </div>

      {/* Dynamic Multi-Column Kanban Workspace */}
      <div className="flex gap-4 overflow-x-auto pb-6">
        {renderedStages.map((stage, stageIdx) => {
          const stageLeads = leads.filter((l) => leadMatchesStage(l.status, stage, stageIdx, renderedStages));
          
          // Smart Sorting
          const sortedLeads = [...stageLeads].sort((a, b) => {
            if (sortBy === "priority") {
              const pWeight: Record<string, number> = { HOT: 3, WARM: 2, COLD: 1 };
              const diff = (pWeight[b.priority || "WARM"] || 2) - (pWeight[a.priority || "WARM"] || 2);
              if (diff !== 0) return diff;
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            }
            if (sortBy === "value") {
              return (b.estimated_deal_value || 0) - (a.estimated_deal_value || 0);
            }
            if (sortBy === "oldest") {
              return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            }
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });

          // Virtualized / Paged Slice for instant DOM rendering
          const limitForStage = columnLimits[stage.id] || 25;
          const visibleLeads = sortedLeads.slice(0, limitForStage);
          const hiddenCount = sortedLeads.length - visibleLeads.length;

          const isTargeted = dragOverStage === stage.id;

          return (
            <div
              key={stage.id}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.id)}
              className={`flex flex-col flex-1 min-w-[270px] rounded-2xl transition-all duration-200 border p-3 h-[75vh] ${
                isTargeted
                  ? "bg-primary/10 border-primary ring-2 ring-primary/30 shadow-lg scale-[1.01]"
                  : "bg-slate-100/70 dark:bg-slate-900/50 border-slate-200/80 dark:border-slate-800/80"
              }`}
            >
              {/* Stage Column Header */}
              <div className="flex items-center justify-between pb-3 mb-2 border-b border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${stage.bg.replace("/10", "")} ${stage.color}`} />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                    {stage.title}
                  </h3>
                </div>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${stage.bg} ${stage.color}`}>
                  {stageLeads.length}
                </span>
              </div>

              {/* Total Stage Value Header Pill */}
              {(() => {
                const aedTotal = stageLeads.filter((l) => l.currency === "AED").reduce((sum, l) => sum + (l.estimated_deal_value || 0), 0);
                const usdTotal = stageLeads.filter((l) => l.currency === "USD").reduce((sum, l) => sum + (l.estimated_deal_value || 0), 0);
                const inrTotal = stageLeads.filter((l) => l.currency === "INR" || (!l.currency && l.currency !== "AED" && l.currency !== "USD")).reduce((sum, l) => sum + (l.estimated_deal_value || 0), 0);

                const parts: string[] = [];
                if (aedTotal > 0) parts.push(`AED ${aedTotal.toLocaleString()}`);
                if (usdTotal > 0) parts.push(`$${usdTotal.toLocaleString()}`);
                if (inrTotal > 0) parts.push(`₹${inrTotal.toLocaleString("en-IN")}`);

                const displayTotal = parts.length > 0 ? parts.join(" • ") : "₹0";

                return (
                  <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-2 px-1 flex items-center justify-between">
                    <span>Stage Value:</span>
                    <span className="font-bold text-slate-800 dark:text-slate-200 text-[11px]">
                      {displayTotal}
                    </span>
                  </div>
                );
              })()}


              {/* Step Automation Hint */}
              <div className="text-[10px] text-slate-500 dark:text-slate-400 px-2 py-1 mb-2.5 rounded bg-white/60 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 truncate" title={stage.automationHint}>
                {stage.automationHint}
              </div>

              {/* Cards Scroll Container - Drop Target Anywhere */}
              <div 
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDrop={(e) => handleDrop(e, stage.id)}
                className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-[160px]"
              >
                {stageLeads.length === 0 ? (
                  <div 
                    onDragOver={(e) => handleDragOver(e, stage.id)}
                    onDrop={(e) => handleDrop(e, stage.id)}
                    className={`h-36 flex flex-col items-center justify-center border-2 border-dashed rounded-xl text-xs transition-all ${
                      isTargeted 
                        ? "border-primary text-primary bg-primary/10 font-bold shadow-sm"
                        : "border-slate-200 dark:border-slate-800 text-slate-400 italic"
                    }`}
                  >
                    {isTargeted ? (
                      <span>Drop anywhere here to move ⚡</span>
                    ) : (
                      <span>No deals in stage</span>
                    )}
                  </div>
                ) : (
                  <>
                    {visibleLeads.map((lead) => {
                      const isDragging = draggingLeadId === lead.id;
                      const priorityColor =
                        lead.priority === "HOT"
                          ? "text-rose-600 bg-rose-500/10 border-rose-500/20"
                          : lead.priority === "WARM"
                          ? "text-amber-600 bg-amber-500/10 border-amber-500/20"
                          : "text-blue-600 bg-blue-500/10 border-blue-500/20";

                      return (
                        <div
                          key={lead.id}
                          draggable={true}
                          onDragStart={(e) => handleDragStart(e, lead.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, stage.id)}
                          onDrop={(e) => handleDrop(e, stage.id)}
                          onClick={() => onSelectLead(lead.id)}
                          className={`group relative p-3.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800/90 hover:border-primary/50 dark:hover:border-primary/50 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${
                            isDragging ? "opacity-30 scale-95 border-dashed border-primary" : ""
                          }`}
                        >
                          {/* Priority, Duplicate & Drag Handle */}
                          <div className="flex items-center justify-between gap-1 mb-2">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${priorityColor}`}>
                              {lead.priority === "HOT" ? "🔥 HOT" : lead.priority === "WARM" ? "🟡 WARM" : "❄️ COLD"}
                            </span>

                            <div className="flex items-center gap-1.5">
                              {lead.is_duplicate === 1 && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-500 border border-rose-500/20">
                                  DUP #{lead.duplicate_of_id}
                                </span>
                              )}
                              {lead.lead_mode === "TEST" && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500">
                                  TEST
                                </span>
                              )}
                              <GripVertical className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 transition-colors" />
                            </div>
                          </div>

                          {/* Client Name & Company */}
                          <div className="flex items-start justify-between gap-1">
                            <h4 className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors line-clamp-1 flex-1">
                              {lead.name}
                            </h4>
                            {onOpenChat && lead.phone && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenChat(lead);
                                }}
                                title="Open Live WhatsApp Chat"
                                className="p-1 rounded-md text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 transition-colors shrink-0 -mt-0.5"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5 line-clamp-1">
                            <Building2 className="w-3 h-3 shrink-0" />
                            {lead.company_name || "Independent"}
                          </p>

                          {/* Visual Stage Progress Bar */}
                          {(() => {
                            const progressData: Record<string, { step: number; pct: number; label: string; barColor: string }> = {
                              "New Lead": { step: 1, pct: 16, label: "Step 1/6 • Intake", barColor: "bg-blue-500" },
                              "Discovery Call": { step: 2, pct: 33, label: "Step 2/6 • Discovery", barColor: "bg-violet-500" },
                              "Proposal Sent": { step: 3, pct: 50, label: "Step 3/6 • Proposal", barColor: "bg-amber-500" },
                              "Negotiation": { step: 4, pct: 75, label: "Step 4/6 • Negotiation", barColor: "bg-orange-500" },
                              "Closed Won": { step: 5, pct: 100, label: "Step 5/6 • Won 🎉", barColor: "bg-emerald-500" },
                              "Closed Lost": { step: 6, pct: 0, label: "Step 6/6 • Archived", barColor: "bg-slate-400" },
                            };
                            const p = progressData[lead.status] || progressData["New Lead"];

                            return (
                              <div className="mt-2.5 space-y-1">
                                <div className="flex items-center justify-between text-[9px] font-semibold text-slate-500 dark:text-slate-400">
                                  <span>{p.label}</span>
                                  <span>{p.pct}%</span>
                                </div>
                                <div className="w-full h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-500 ${p.barColor}`}
                                    style={{ width: `${p.pct}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })()}

                          {/* Automated Action Badge Triggered */}
                          {lead.status === "Discovery Call" && (
                            <div className="mt-2 text-[9px] px-2 py-1 rounded bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20 flex items-center gap-1 font-medium">
                              <span>⚡ Discovery invite sent • 48h follow-up</span>
                            </div>
                          )}
                          {lead.status === "Proposal Sent" && (
                            <div className="mt-2 text-[9px] px-2 py-1 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 flex items-center gap-1 font-medium">
                              <span>⚡ Scope ready sent • 72h review alert</span>
                            </div>
                          )}
                          {lead.status === "Negotiation" && (
                            <div className="mt-2 text-[9px] px-2 py-1 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20 flex items-center gap-1 font-medium">
                              <span>🔥 Auto-escalated to HOT • Closing alert</span>
                            </div>
                          )}
                          {lead.status === "Closed Won" && (
                            <div className="mt-2 text-[9px] px-2 py-1 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center gap-1 font-medium">
                              <span>🎉 Deal Won! Onboarding packet sent</span>
                            </div>
                          )}
                          {lead.status === "Closed Lost" && (
                            <div className="mt-2 text-[9px] px-2 py-1 rounded bg-slate-500/10 text-slate-500 border border-slate-500/20 flex items-center gap-1 font-medium">
                              <span>❄️ Demoted to COLD • Lead archived</span>
                            </div>
                          )}

                          {/* Service Category & Deal Value */}
                          <div className="mt-2.5 flex items-center justify-between text-[11px]">
                            <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium truncate max-w-[120px]" title={lead.service_type}>
                              {lead.service_type}
                            </span>
                            <div className="text-right flex flex-col items-end">
                              {lead.quantity ? (
                                <span className="text-[10px] font-bold text-primary">
                                  {lead.quantity.toLocaleString()} {lead.unit || "Units"}
                                </span>
                              ) : null}
                              {lead.estimated_deal_value > 0 ? (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 border border-slate-200/60 dark:border-slate-700/60">
                                    {getCurrencyBadge(lead.currency)}
                                  </span>
                                  <span className="font-bold text-emerald-600 dark:text-emerald-400">
                                    {formatCurrencyValue(lead.estimated_deal_value, lead.currency)}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[10px] text-slate-400">Quote Req</span>
                              )}
                            </div>
                          </div>

                          {/* Follow-up reminder if present */}
                          {lead.next_followup_date && (
                            <div className="mt-2 text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                              <span>📅 Due: {lead.next_followup_date}</span>
                            </div>
                          )}

                          {/* Rep and Quick Stage Shift Controls */}
                          <div
                            className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[10px] text-slate-400"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="truncate max-w-[90px]">
                              {lead.assigned_rep || "Unassigned"}
                            </span>

                            <div className="flex items-center gap-1">
                              {stageIdx > 0 && (
                                <button
                                  title={`Move back to ${STAGES[stageIdx - 1].title}`}
                                  onClick={() => onStatusChange(lead.id, STAGES[stageIdx - 1].id)}
                                  className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-white transition-colors"
                                >
                                  <ChevronLeft className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {stageIdx < STAGES.length - 1 && (
                                <button
                                  title={`Advance to ${STAGES[stageIdx + 1].title}`}
                                  onClick={() => onStatusChange(lead.id, STAGES[stageIdx + 1].id)}
                                  className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-white transition-colors"
                                >
                                  <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Column Pagination Expand Button */}
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          setColumnLimits((prev) => ({
                            ...prev,
                            [stage.id]: (prev[stage.id] || 25) + 50,
                          }))
                        }
                        className="w-full py-2 px-3 rounded-xl bg-slate-200/70 dark:bg-slate-800/70 text-slate-700 dark:text-slate-300 text-[11px] font-bold hover:bg-primary/10 hover:text-primary transition-all border border-slate-300/60 dark:border-slate-700/60 flex items-center justify-center gap-1.5 shadow-sm"
                      >
                        Load More (+{hiddenCount} leads)
                      </button>
                    )}

                    {/* Dedicated Bottom Drop Zone - Drop Anywhere */}
                    <div
                      onDragOver={(e) => handleDragOver(e, stage.id)}
                      onDrop={(e) => handleDrop(e, stage.id)}
                      className={`w-full py-3.5 px-2 rounded-xl border-2 border-dashed transition-all flex items-center justify-center text-center text-[10px] ${
                        isTargeted
                          ? "border-primary bg-primary/15 text-primary font-bold shadow-inner"
                          : "border-slate-200/50 dark:border-slate-800/50 text-slate-400/60 hover:border-slate-300 dark:hover:border-slate-700"
                      }`}
                    >
                      {isTargeted ? (
                        <span>⚡ Drop here to move to {stage.title}</span>
                      ) : (
                        <span>+ Drop anywhere in column</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
